import {Component, DestroyRef, inject, Input, OnInit} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
    selectActiveAssetValuation,
    selectAllHistoryRecords,
    selectFinancialOperationalValuation,
    selectNotAssignedCapex,
    selectOperationalValuationEdit,
    selectSelectedValuationError
} from "../../../store/asset-valuation/asset-valuation.selectors";
import {Store} from "@ngrx/store";
import {Actions, ofType} from "@ngrx/effects";
import {AssetValuationScenario} from "../../../models/asset-valuation/asset-valuation-scenario";
import {FormBuilder, Validators} from "@angular/forms";
import {CodeTableEnum} from "../../../../shared/model/code";
import {selectCodesFromTable} from "../../../../shared/store/shared.selectors";
import {AssetValuationDetailActions} from "../../../store/asset.actions";
import {
    selectGeneralPartnerNav,
    selectIsAnyAssetFormInEditMode,
    selectIsOwnershipStakeMissing,
    selectRelatedFundCurrencyIsoCode,
    selectSelectedAssetCurrencyIsoCode
} from "../../../store/asset/asset.selectors";
import {combineLatest, distinctUntilChanged, filter, map, Observable, of, switchMap, take} from "rxjs";
import {FxActions} from "../../../../shared/store/shared.actions";
import {selectFxAutofillRate, selectFxLoadingMode} from "../../../../shared/store/fx/fx.selectors";
import {selectSettingsLoaded} from "../../../../shared/user-setting/store/user-setting.selectors";
import {FxAutofillSlice, fxAutofillSource, isFxRateEmpty} from "../../../../shared/store/fx/fx-autofill";
import {DateUtil} from "../../../../shared/utils/date-util";
import {AssetValuation, EMPTY_ASSET_VALUATION} from "../../../models/asset-valuation/asset-valuation";
import {numberNotBiggerThan190000m} from "../../../../shared/utils/form-validators";
import {DecimalFormatPipe} from "../../../../shared/pipes/decimal-format/decimal-format.pipe";
import {AssetCashflowNewDialogComponent} from "../asset-cashflow-new-dialog/asset-cashflow-new-dialog.component";
import {AssetHistoryBaseCustomDialogComponent} from "./asset-history-base-custom-dialog/asset-history-base-custom-dialog.component";
import {AssetValuationTypeEnum} from "../../../models/asset-valuation/asset-valuation-type.enum";
import {MatDialog} from "@angular/material/dialog";
import {FinancialPerformanceRecord} from "../../../models/asset/financial-performance-record";
import {EarningBaseCode} from "../../../../shared/model/earning-base-code";
import {ConfirmationComponent} from "../../../../shared/components/confirmation/confirmation.component";
import {PeriodUtil} from "../../../../shared/utils/period-util";
import {AssetValuationHistoryBase} from "../../../models/asset-valuation/asset-valuation-history-base";
import {PendingHistoryBaseChange} from "../../../generated-sources/api";

type PendingHistoryBaseChangeWithPreview = PendingHistoryBaseChange & { previewLabel?: string };

@Component({
    selector: "valumize-asset-financial-operational-valuation",
    templateUrl: "./asset-financial-operational-valuation.component.html",
    styleUrls: ["./asset-financial-operational-valuation.component.scss"],
    standalone: false
})
export class AssetFinancialOperationalValuationComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);
    private readonly dialog = inject(MatDialog);
    private readonly actions$ = inject(Actions);
    private readonly destroyRef = inject(DestroyRef);

    @Input() assetId?: number;

    pendingHistoryBaseChange: PendingHistoryBaseChangeWithPreview | null = null;

    /** Bound value of the "Select period…" dropdown; reset to null after each pick so it stays a one-shot command. */
    historyRecordSelectValue: string | number | null = null;

    /** Tooltip shown on the info icon of a history record that can't be used as a base (WI 1167, WI 1365). */
    readonly historyRecordIncompleteMessage =
        "This period can not be used as a history base - Revenue or Calc Base is missing";

    /** FOV-card banner + Net-Debt-row icon message when Net Debt was never entered for the history base (WI 998). */
    readonly netDebtAssumedZeroMessage =
        "Net Debt has not been entered for the historical periods. It is assumed to be 0 for calculation purposes.";

    /** True when a history base is selected but its Net Debt is blank — it is then assumed 0 for calculations (WI 998). */
    isNetDebtAssumedZero(historyBase?: AssetValuationHistoryBase | null): boolean {
        return !!historyBase && historyBase.netDebt?.amount == null;
    }

    /** Tooltip on the info icon of a still-selectable history record that has no Enterprise Value (WI 1365). */
    readonly historyRecordEnterpriseValueMissingMessage =
        "Enterprise Value is missing - the record can be used, but Exit Proceeds & Bid Price will be 0 until EV is entered";

    financialOperationalValuation$ = this.store.select(selectFinancialOperationalValuation);
    isEditDisabled$ = this.store.select(selectIsAnyAssetFormInEditMode);
    fundInvestmentNav$ = this.store.select(selectGeneralPartnerNav);
    isOwnershipStakeMissing$ = this.store.select(selectIsOwnershipStakeMissing);
    notAssignedCapex$: Observable<{ LOW: number; BASE: number; HIGH: number }> = this.store.select(selectNotAssignedCapex);
    fovError$ = this.store.select(selectSelectedValuationError);
    availableHistoryRecords$: Observable<FinancialPerformanceRecord[]> = this.store.select(selectAllHistoryRecords);

    persistedAssetValuation: AssetValuation = EMPTY_ASSET_VALUATION;
    codeTableEarningBase = CodeTableEnum.ASSET_EARNING_BASE;
    selectCodesEarningBase$ = this.store.select(selectCodesFromTable(this.codeTableEarningBase));

    // Inline FX autofill (US-1171)
    fxAutofill$: Observable<FxAutofillSlice | null> = of(null);
    fxAutofillAsOfDate: string | null = null;
    fxAutofilledUntouched = false;
    fxEditedDate: Date | null = null;
    private fxForceOverwrite = false;
    private valuationIsEditable = false;

    operationalValuationForm = this.formBuilder.group({
        earningBase: this.formBuilder.control<string>("", {nonNullable: true, validators: [Validators.required]}),
        exitDateAssumption: this.formBuilder.control<Date | null>(null, {validators: [Validators.required]}),
        requiredGrossIRR: this.formBuilder.control<number | null>(null, {validators: [Validators.required, numberNotBiggerThan190000m]}),
        fxRateFund: this.formBuilder.control<number | null>(null, {validators: [Validators.required, numberNotBiggerThan190000m]})
    });

    ngOnInit() {
        this.store.select(selectActiveAssetValuation).pipe(
            takeUntilDestroyed(this.destroyRef),
            map((assetValuation) => {
                this.persistedAssetValuation = assetValuation.data;

                this.operationalValuationForm.patchValue({
                    earningBase: assetValuation.data.earningBase.code,
                    exitDateAssumption: DateUtil.toJsDate(assetValuation.data.exitDateAssumption.date),
                    requiredGrossIRR: DecimalFormatPipe.transformFractionToPercent(assetValuation.data.requiredGrossIRR.fraction ?? undefined)
                });

                // Re-emit guard (US-1171): while editing, the fxRateFund control may hold an unsaved
                // autofill suggestion — do not clobber it with the persisted value on store re-emits.
                if (!this.valuationIsEditable) {
                    this.operationalValuationForm.controls.fxRateFund.patchValue(assetValuation.data.fxRateFund.fraction ?? null);
                }
            })
        ).subscribe();

        // FX autofill trigger (US-1171): cache the edit flag (read by the re-emit guard) and run
        // autofill when the form transitions into edit mode.
        this.store.select(selectOperationalValuationEdit).pipe(
            map((edit) => edit.valuationIsEditable),
            distinctUntilChanged(),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((editable) => {
            this.valuationIsEditable = editable;
            if (editable) {
                this.runFxAutofill();
            } else {
                // Cancel/save returns to read-only: discard the in-progress autofill provenance so a
                // canceled "as of …" suggestion does not linger into the next edit session (US-1171).
                this.fxAutofilledUntouched = false;
                this.fxAutofillAsOfDate = null;
                this.fxEditedDate = null;
                this.fxForceOverwrite = false;
            }
        });

        // Once the user edits the autofilled rate, drop the "untouched" provenance (save as "user").
        this.operationalValuationForm.controls.fxRateFund.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            if (!this.fxForceOverwrite) {
                this.fxAutofilledUntouched = false;
                this.fxEditedDate = new Date();
            }
        });

        this.actions$.pipe(
            ofType(AssetValuationDetailActions.saveSuccess),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => this.pendingHistoryBaseChange = null);
    }

    private runFxAutofill(): void {
        this.store.select(selectSettingsLoaded).pipe(
            filter(Boolean),
            take(1),
            switchMap(() => combineLatest([
                this.store.select(selectFxLoadingMode),
                this.store.select(selectSelectedAssetCurrencyIsoCode),
                this.store.select(selectRelatedFundCurrencyIsoCode)
            ]).pipe(take(1))),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(([mode, assetCcy, fundCcy]) => {
            const control = this.operationalValuationForm.controls.fxRateFund;
            const isEmpty = isFxRateEmpty(control.value);
            if (mode !== "AUTOMATIC") {
                return;
            }
            if (!assetCcy || !fundCcy) {
                return;
            }
            if (!isEmpty && !this.fxForceOverwrite) {
                return;
            }
            if (assetCcy === fundCcy) {
                control.patchValue(1);
                this.fxAutofilledUntouched = true;
                this.fxAutofillAsOfDate = null;
                this.fxForceOverwrite = false;
                return;
            }
            const rate$ = this.store.select(selectFxAutofillRate(assetCcy, fundCcy));
            this.fxAutofill$ = rate$;
            this.store.dispatch(FxActions.fetchAutofillRate({from: assetCcy, to: fundCcy}));
            this.subscribeFxAutofillResult(rate$);
        });
    }

    private subscribeFxAutofillResult(rate$: Observable<FxAutofillSlice | null>): void {
        rate$.pipe(
            filter((slice): slice is FxAutofillSlice => slice?.status === "LOADED" || slice?.status === "ERROR"),
            take(1),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((slice) => {
            const control = this.operationalValuationForm.controls.fxRateFund;
            const canPatch = isFxRateEmpty(control.value) || this.fxForceOverwrite;
            if (slice.status === "LOADED" && slice.data.fraction != null && canPatch) {
                control.patchValue(slice.data.fraction);
                this.fxAutofilledUntouched = true;
                this.fxAutofillAsOfDate = slice.data.asOfDate;
                this.fxForceOverwrite = false;
            }
            // ERROR or LOADED-without-fraction (unsupported pair): leave the control; the template
            // surfaces the inline error from fxAutofill$.
        });
    }

    refreshFxRate = () => {
        this.fxForceOverwrite = true;
        this.runFxAutofill();
    };

    save = () => {
        const exitDateAssumption = DateUtil.toIsoDate(this.operationalValuationForm.controls.exitDateAssumption.value)
            ?? this.persistedAssetValuation.exitDateAssumption.date;
        const requiredGrossIrr = DecimalFormatPipe.transformPercentToFraction(this.operationalValuationForm.controls.requiredGrossIRR.value)
            ?? this.persistedAssetValuation.requiredGrossIRR.fraction;
        const earningBase = this.operationalValuationForm.controls.earningBase.value ?? this.persistedAssetValuation.earningBase.code;
        const fxRateFundValue = this.operationalValuationForm.controls.fxRateFund.value;
        const fxRateFund = fxRateFundValue ?? this.persistedAssetValuation.fxRateFund.fraction;

        // Provenance (US-1171): an untouched autofilled rate persists its ECB as-of date; a rate the
        // user typed/edited persists "user"; an unchanged rate keeps its existing source.
        const fxRateFundChanged = fxRateFundValue != null && fxRateFundValue !== this.persistedAssetValuation.fxRateFund.fraction;
        const fxRateFundSource = fxAutofillSource(
            this.fxAutofilledUntouched,
            this.fxAutofillAsOfDate,
            fxRateFundChanged,
            this.persistedAssetValuation.fxRateFund.source
        );

        const assetValuationToSave: AssetValuation = {
            ...this.persistedAssetValuation,
            exitDateAssumption: {
                ...this.persistedAssetValuation.exitDateAssumption,
                date: exitDateAssumption
            },
            earningBase: {
                ...this.persistedAssetValuation.earningBase,
                code: earningBase
            },
            requiredGrossIRR: {
                ...this.persistedAssetValuation.requiredGrossIRR,
                fraction: requiredGrossIrr
            },
            fxRateFund: {
                ...this.persistedAssetValuation.fxRateFund,
                fraction: fxRateFund,
                source: fxRateFundSource
            }
        };

        const pending = this.pendingHistoryBaseChange;
        const dispatchSave = () => {
            const pendingDto = pending ? toPendingDto(pending) : undefined;
            this.store.dispatch(AssetValuationDetailActions.save({
                assetValuation: assetValuationToSave,
                pendingHistoryBaseChange: pendingDto
            }));
        };

        // Only confirm when an existing history base is being replaced or removed; on a
        // first-time selection there is nothing to reset (no calc fields, no forecast records,
        // no cashflows can exist without a prior history base).
        if (pending && this.persistedAssetValuation.historyBase) {
            const dialogRef = this.dialog.open(ConfirmationComponent);
            dialogRef.componentInstance.confirmMessage = "Save and apply history base change?";
            dialogRef.componentInstance.htmlContent =
                "Saving will apply the pending history base change and reset all calculated values, " +
                "scenarios, and forecast records. User-entered parameters will be preserved.<br><br>" +
                "Do you want to continue?";
            dialogRef.afterClosed().pipe(
                take(1),
                filter(result => !!result),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe(() => dispatchSave());
        } else {
            dispatchSave();
        }
    };

    editMode = () => this.store.dispatch(AssetValuationDetailActions.edit());

    cancel = () => {
        this.pendingHistoryBaseChange = null;
        this.store.dispatch(AssetValuationDetailActions.cancel());
    };

    clearPendingHistoryBaseChange = () => {
        this.pendingHistoryBaseChange = null;
    };

    findScenario(scenarios: AssetValuationScenario[], scenarioCode: string): AssetValuationScenario | undefined {
        return scenarios.find(scenario => scenario.scenario.code === scenarioCode);
    }

    validateRequiredFieldsForCalc(assetValuation: AssetValuation) {
        return !(
            !assetValuation.earningBase.code
            || !assetValuation.exitDateAssumption.date
            || !assetValuation.requiredGrossIRR.fraction
            || !assetValuation.fxRateFund.fraction
            || !assetValuation.historyBase
        );
    }

    hasRequiredFieldsExceptHistoryBase(assetValuation: AssetValuation): boolean {
        return !!(
            assetValuation.earningBase.code
            && assetValuation.exitDateAssumption.date
            && assetValuation.requiredGrossIRR.fraction
            && assetValuation.fxRateFund.fraction
        );
    }

    openNewCashflowDialog(scenarios: AssetValuationScenario[]) {
        const dialogRef = this.dialog.open(AssetCashflowNewDialogComponent);
        dialogRef.componentInstance.assetValuation = this.persistedAssetValuation;
        dialogRef.componentInstance.scenarios = scenarios;
        dialogRef.componentInstance.valuationType = AssetValuationTypeEnum.FinancialOperationalValuation;
    }

    onEditRationale = () => this.store.dispatch(AssetValuationDetailActions.editRationale());

    onCancelRationale = () => this.store.dispatch(AssetValuationDetailActions.cancelRationale());

    onSaveRationale = (assetValuation: AssetValuation, rationale: string) => {
        if (assetValuation.id && assetValuation.valuationType.code) {
            this.store.dispatch(AssetValuationDetailActions.saveRationale({
                assetValuationId: assetValuation.id,
                rationale,
                valuationType: assetValuation.valuationType.code
            }));
        }
    }

    navExists = (nav?: number | null) => nav !== undefined && nav !== null;

    onResetValuation = () => {
        const dialogRef = this.dialog.open(ConfirmationComponent);
        dialogRef.componentInstance.confirmMessage = "Reset Valuation?";
        dialogRef.componentInstance.htmlContent =
            "This will clear all calculated values, scenarios, and forecast records. " +
            "User-entered parameters (Earning Base, Exit Date, Required gIRR, FX Rate, History Base Period) " +
            "will be preserved.";

        dialogRef.afterClosed().pipe(
            take(1),
            filter(result => !!result),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            if (this.assetId && this.persistedAssetValuation.id) {
                this.store.dispatch(AssetValuationDetailActions.resetValuation({
                    assetId: this.assetId,
                    assetValuationId: this.persistedAssetValuation.id
                }));
            }
        });
    };

    onHistoryRecordSelected = (value: string | number): void => {
        // One-shot command: reset the dropdown so it shows the placeholder again (a pick either sets a
        // pending change — which hides the dropdown — or opens the custom dialog, which may be cancelled).
        this.historyRecordSelectValue = null;

        if (value === "__custom__") {
            this.onCreateCustomRecord();
            return;
        }

        const historyRecordId = Number(value);
        if (!historyRecordId) {
            return;
        }

        this.availableHistoryRecords$.pipe(
            take(1),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(records => {
            const record = records.find(r => r.id === historyRecordId);
            const previewLabel = record ? this.getHistoryRecordLabel(record) : String(historyRecordId);
            this.pendingHistoryBaseChange = {
                kind: PendingHistoryBaseChange.KindEnum.SelectRecord,
                historyRecordId,
                previewLabel
            };
        });
    };

    onCreateCustomRecord = (): void => {
        const earningBase = this.operationalValuationForm.controls.earningBase.value || null;
        const dialogRef = this.dialog.open(AssetHistoryBaseCustomDialogComponent, {
            data: {earningBase},
            panelClass: "asset-history-base-custom-dialog-panel"
        });
        dialogRef.afterClosed().pipe(
            take(1),
            filter(result => !!result),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((request) => {
            const previewLabel = request.displayName?.trim() || PeriodUtil.formatPeriodLabel(request.period, false);
            this.pendingHistoryBaseChange = {
                kind: PendingHistoryBaseChange.KindEnum.CreateCustom,
                customRequest: request,
                previewLabel
            };
        });
    };

    onDeleteHistoryBase = (): void => {
        this.pendingHistoryBaseChange = {
            kind: PendingHistoryBaseChange.KindEnum.Delete
        };
    };

    /**
     * A financial-history record can be used as the FOV history base only when it carries the inputs the
     * calculation needs: Revenue and the earning-base amount ("Calc Base" — the record field matching the
     * selected Earning Base, e.g. EBITDA). Records missing either are shown but disabled (WI 1167). Enterprise
     * Value is no longer required here — EV scraping is unreliable and was blocking otherwise-usable records
     * from being selected (WI 1365).
     */
    isHistoryRecordSelectable = (record: FinancialPerformanceRecord): boolean =>
        record.revenue?.amount != null
        && this.getCalcBaseAmount(record) != null;

    /**
     * A selectable record whose Enterprise Value is blank: allowed, but flagged in the dropdown so the user knows
     * the valuation will come out with 0 Exit Proceeds / Bid Price until EV is filled in (WI 1365).
     */
    isHistoryRecordEnterpriseValueMissing = (record: FinancialPerformanceRecord): boolean =>
        this.isHistoryRecordSelectable(record) && record.enterpriseValue?.amount == null;

    /** The record's amount for the currently selected Earning Base (the "Calc Base"); null/undefined when unset. */
    private readonly getCalcBaseAmount = (record: FinancialPerformanceRecord): number | null | undefined => {
        switch (this.operationalValuationForm.controls.earningBase.value) {
            case EarningBaseCode.EBT:
                return record.ebt?.amount;
            case EarningBaseCode.EBIT:
                return record.ebit?.amount;
            case EarningBaseCode.EBITA:
                return record.ebita?.amount;
            case EarningBaseCode.EBITDA:
                return record.ebitda?.amount;
            case EarningBaseCode.EBITDAR:
                return record.ebitdar?.amount;
            default:
                return undefined; // no earning base selected → Calc Base can't be resolved
        }
    };

    getHistoryRecordLabel = (record: FinancialPerformanceRecord): string => {
        const willAnnualize = ["QUARTER", "SEMESTER", "YEAR_TO_DATE"].includes(record.period.type ?? "");
        return PeriodUtil.formatPeriodLabel(record.period, willAnnualize);
    };

    // A base is "custom" only if the user created it as such. After origin deletion,
    // checkAndMarkStale orphan-clears originHistoryRecordId AND sets stale=true; without
    // the !stale check those orphan-cleared bases would be misclassified as custom and
    // render via displayName instead of the period label.
    isCustomHistoryBase = (historyBase: AssetValuationHistoryBase): boolean =>
        !historyBase.annualized && !historyBase.originHistoryRecordId && !historyBase.stale;

    getHistoryBaseLabel = (historyBase: AssetValuationHistoryBase): string => {
        if (this.isCustomHistoryBase(historyBase)) {
            return historyBase.displayName;
        }
        return PeriodUtil.formatPeriodLabel(historyBase.period, historyBase.annualized);
    };
}

function toPendingDto(pending: PendingHistoryBaseChangeWithPreview): PendingHistoryBaseChange {
    return {
        kind: pending.kind,
        ...(pending.historyRecordId !== undefined ? {historyRecordId: pending.historyRecordId} : {}),
        ...(pending.customRequest !== undefined ? {customRequest: pending.customRequest} : {})
    };
}
